import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter
} from '@chakra-ui/react';
import { AxiosResponse, HttpStatusCode } from 'axios';
import React, { useState, useEffect, useMemo } from 'react';
import { Select } from 'chakra-react-select';
import { useAlertContext } from '../../../../../context/AlertProvider';
import { useApi } from '../../../../../context/ApiProvider';
import useAuth from '../../../../../hooks/useAuth';
import { CustomConsumerInterface } from '../../../../../shared/type/consumerCreationContext.type';
import { NewKidInterface } from '../../../../../shared/type/kid.type';
import CustomChakraButton from '../../../../CustomChakraButton/CustomChakraButton';
import CustomInput from '../../../../CustomInput/CustomInput';
import IconComponent from '../../../../IconComponent/IconComponent';
import { useSafeTranslation } from '../../../../../hooks/useSafeTranslation';
import { ConsumerDto } from '../../../../../shared/type/parentDashboardContext.type';

const customStyles = {
  control: (provided: any) => ({
    ...provided,
    borderRadius: '8px',
    fontFamily: 'Arial, sans-serif',
    fontSize: '16px',
    padding: '4px' + '',
    borderColor: '#ccc',
    boxShadow: 'none',
    '&:hover': {
      borderColor: '#aaa'
    }
  }),
  option: (provided: any, state: any) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#FFF7E6' : 'white',
    color: '#101828',
    padding: '8px',
    borderRadius: '8px'
  }),
  singleValue: (provided: any) => ({
    ...provided,
    fontFamily: 'Arial, sans-serif',
    fontSize: '16px',
    color: '#333'
  })
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  pendingKid?: ConsumerDto | null;
}

const ParentAddKidPopUp = ({ isOpen, onClose, pendingKid }: Props) => {
  const { t } = useSafeTranslation();
  const { apiParentConsumerController, apiParentConsumerCandidateController } = useApi();
  const { auth } = useAuth();
  const { setShow, setAlertProperties } = useAlertContext();
  const [consumerCreationContext, setConsumerCreationContext] = useState<CustomConsumerInterface>();
  const [chosenTenantId, setChosenTenantId] = useState<number | undefined>(undefined);
  const classes = useMemo(
    () =>
      consumerCreationContext?.orgUnits.map((unit) => ({
        ...unit,
        label: unit.name,
        value: unit.id
      })),
    [consumerCreationContext?.orgUnits]
  );
  const [newKid, setNewKid] = useState<NewKidInterface>({
    tenantId: pendingKid?.tenantId || chosenTenantId || null,
    firstName: pendingKid?.firstName || '',
    lastName: pendingKid?.lastName || '',
    organisationUnitId: pendingKid?.organisationUnitId || null,
    organisationUnitName: pendingKid?.organisationUnitName || null,
    cardNumber: pendingKid?.cardNumber || '',
    remarks: pendingKid?.remarks || '',
    parentIds: [auth.loggedUserDto.id]
  });
  const selectedClasses = useMemo(
    () => classes?.find((unit) => unit.id === newKid.organisationUnitId),
    [newKid.organisationUnitId, classes]
  );
  const selectedTenant = auth?.availableTenants
    .map((unit) => ({
      ...unit,
      label: unit.name,
      value: unit.id
    }))
    .find((tenant) => tenant.id === newKid.tenantId);

  const fetchCreateConsumerContext = async () => {
    try {
      const response: AxiosResponse<CustomConsumerInterface> = await apiParentConsumerController(
        'consumer-creation-context'
      ).get(`/${newKid?.tenantId || chosenTenantId}`);
      setConsumerCreationContext(response.data);
    } catch (error: any) {}
  };

  const addKid = async (e: React.SyntheticEvent) => {
    e.preventDefault();

    setNewKid((prev) => ({ ...prev, tenantId: newKid?.tenantId || chosenTenantId || 0 }));

    try {
      const response = await apiParentConsumerCandidateController('create-candidate').post(
        '',
        newKid
      );

      if (response.status === HttpStatusCode.Ok) {
        setAlertProperties({
          timeout: 9000,
          title: t('parent_add_kid_popup.success_title'),
          status: 'success'
        });
        onClose();
      }
    } catch (error: any) {
      if (error.response.data.errorMessage) {
        setAlertProperties({
          timeout: 9000,
          title: t('parent_add_kid_popup.error_title'),
          description: t('parent_add_kid_popup.error_message', {
            error: error.response.data.errorMessage
          }),
          status: 'error'
        });
      } else {
        const { message, fieldName } = error.response.data.violations[0];
        setAlertProperties({
          timeout: 9000,
          title: t('parent_add_kid_popup.error_title'),
          description: t('parent_add_kid_popup.error_field_message', { field: fieldName, message }),
          status: 'error'
        });
      }
    } finally {
      setShow(true);
    }
  };

  const updateKid = async (e: React.SyntheticEvent) => {
    e.preventDefault();

    setNewKid((prev) => ({ ...prev, tenantId: newKid?.tenantId || chosenTenantId || 0 }));

    try {
      const response = await apiParentConsumerCandidateController('update-candidate').put(
        `/${pendingKid?.id}`,
        newKid
      );

      if (response.status === HttpStatusCode.Ok) {
        setAlertProperties({
          timeout: 9000,
          title: t('parent_add_kid_popup.edited_success_title'),
          status: 'success'
        });
        onClose();
      }
    } catch (error: any) {
      if (error.response.data.errorMessage) {
        setAlertProperties({
          timeout: 9000,
          title: t('parent_add_kid_popup.edited_error_title'),
          description: t('parent_add_kid_popup.error_message', {
            error: error.response.data.errorMessage
          }),
          status: 'error'
        });
      } else {
        if (!error.response.data.violations || error.response.data.violations?.length === 0) {
          console.error('Something went wrong');
          setAlertProperties({
            timeout: 9000,
            title: 'Coś poszło nie tak',
            status: 'error'
          });
          return;
        }
        const { message, fieldName } = error.response.data.violations[0];
        setAlertProperties({
          timeout: 9000,
          title: t('parent_add_kid_popup.edited_error_title'),
          description: t('parent_add_kid_popup.error_field_message', { field: fieldName, message }),
          status: 'error'
        });
      }
    } finally {
      setShow(true);
    }
  };

  useEffect(() => {
    if (chosenTenantId !== undefined || !!pendingKid?.tenantId) {
      fetchCreateConsumerContext();
    }

    if (auth.availableTenants.length === 1) {
      const tenantId = auth.availableTenants[0].id;
      setNewKid((prev) => ({ ...prev, tenantId }));
      setChosenTenantId(tenantId);
    }
  }, [chosenTenantId]);

  useEffect(() => {
    if (pendingKid && classes) {
      const matchedClass = classes.find((unit) => unit.name === pendingKid.organisationUnitName);
      if (matchedClass) {
        setNewKid((prev) => ({
          ...prev,
          organisationUnitId: matchedClass.id
        }));
      }
    }
  }, []);

  return (
    <Modal closeOnOverlayClick={false} isOpen={isOpen} onClose={onClose} size="xl" isCentered>
      <form onSubmit={!pendingKid ? addKid : updateKid}>
        <ModalOverlay
          sx={{
            backdropFilter: 'blur(10px)',
            backgroundColor: 'rgba(0, 0, 0, 0.5)'
          }}
        />
        <ModalContent>
          <ModalHeader className="flex flex-col gap-spacing-xl">
            <div className="w-12 h-12 bg-brand-100 rounded-full flex items-center justify-center">
              <IconComponent iconName="userPlus" className="w-5 h-4.5" color="#FFAC05" />
            </div>
            <div className="flex flex-col gap-spacing-xs">
              <h2 className="font-semibold text-lg">{t('parent_add_kid_popup.title')}</h2>
              <p className="font-normal text-sm text-grayLight-600">
                {t('parent_add_kid_popup.subtitle')}
              </p>
            </div>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody className="grid gap-spacing-3xl">
            <CustomInput
              inputParams={{
                name: 'firstName',
                onChange: (e) => {
                  setNewKid((prev) => {
                    return { ...prev, [e.target.name]: e.target.value };
                  });
                },
                required: true,
                value: newKid.firstName
              }}
              type="default"
              size="md"
              destructive={false}
              label={t('parent_add_kid_popup.first_name')}
            />
            <CustomInput
              inputParams={{
                name: 'lastName',
                value: newKid.lastName,
                onChange: (e) => {
                  setNewKid((prev) => ({ ...prev, [e.target.name]: e.target.value }));
                },
                required: true
              }}
              type="default"
              size="md"
              destructive={false}
              label={t('parent_add_kid_popup.last_name')}
            />
            {auth.availableTenants.length > 1 && (
              <div className="flex flex-col gap-spacing-md">
                <label className="text-sm font-medium text-grayLight-700">
                  {t('parent_add_kid_popup.school')}*
                </label>
                <Select
                  placeholder={t('parent_add_kid_popup.select_placeholder')}
                  isRequired
                  styles={customStyles}
                  value={selectedTenant}
                  onChange={(selectedOption) => {
                    if (selectedOption) {
                      setChosenTenantId(selectedOption.value);
                      setNewKid((prev) => ({
                        ...prev,
                        tenantId: selectedOption.value,
                        organisationUnitId: null,
                        organisationUnitName: ''
                      }));
                    }
                  }}
                  options={auth?.availableTenants.map((unit) => ({
                    ...unit,
                    label: unit.name,
                    value: unit.id
                  }))}
                />
              </div>
            )}
            <div className="flex flex-col gap-spacing-md">
              <label className="text-sm font-medium text-grayLight-700">
                {t('parent_add_kid_popup.class')}*
              </label>
              <Select
                isRequired
                placeholder={t('parent_add_kid_popup.select_placeholder')}
                styles={customStyles}
                value={selectedClasses}
                onChange={(selectedOption) => {
                  if (selectedOption) {
                    setNewKid((prev) => ({
                      ...prev,
                      organisationUnitId: selectedOption.value || null,
                      organisationUnitName: selectedOption.label || null
                    }));
                  }
                }}
                options={consumerCreationContext?.orgUnits.map((unit) => ({
                  ...unit,
                  label: unit.name,
                  value: unit.id
                }))}
              />
            </div>
            <CustomInput
              type="default"
              size="md"
              destructive={false}
              label={t('parent_add_kid_popup.identifier_number')}
              inputParams={{
                name: 'cardNumber',
                value: newKid.cardNumber || '',
                placeholder: t('parent_add_kid_popup.identifier_placeholder'),
                onChange: (e) => {
                  const target = e.target as HTMLInputElement;
                  const value = target.value.replace(/\D/g, '');
                  setNewKid((prev) => ({ ...prev, [target.name]: value }));
                },
                onInput: (e) => {
                  const target = e.target as HTMLInputElement;
                  target.value = target.value.replace(/\D/g, '');
                },
                pattern: '\\d{10}',
                title: t('parent_add_kid_popup.identifier_validation'),
                maxLength: 10,
                required: false
              }}
            />
            <div className="flex flex-col gap-spacing-xs">
              <CustomInput
                type="default"
                size="md"
                destructive={false}
                label={t('parent_add_kid_popup.remarks')}
                inputParams={{
                  name: 'remarks',
                  value: newKid.remarks || '',
                  placeholder: t('parent_add_kid_popup.remarks_placeholder'),
                  onChange: (e) => {
                    setNewKid((prev) => ({ ...prev, [e.target.name]: e.target.value }));
                  },
                  maxLength: 500,
                  required: false
                }}
              />
              <div className="flex justify-end">
                <span className="font-normal text-sm text-grayLight-600">
                  {newKid.remarks?.length}/500
                </span>
              </div>
            </div>
          </ModalBody>
          <ModalFooter className="w-full flex justify-between gap-spacing-lg">
            <CustomChakraButton
              hierarchy="secondaryColor"
              size="lg"
              iconPosition="onlyText"
              buttonProps={{
                className: 'grow',
                type: 'button',
                onClick: onClose
              }}>
              {t('parent_add_kid_popup.back')}
            </CustomChakraButton>
            <CustomChakraButton
              hierarchy="primary"
              size="lg"
              iconPosition="onlyText"
              buttonProps={{
                className: 'grow',
                type: 'submit'
              }}>
              {t('parent_add_kid_popup.save')}
            </CustomChakraButton>
          </ModalFooter>
        </ModalContent>
      </form>
    </Modal>
  );
};

export default ParentAddKidPopUp;
